"use client"

import Link from "next/link"
import { usePathname } from "next/navigation"
import { useEffect, useState } from "react"
import { useAuth } from "@/lib/auth"
import { api } from "@/lib/api"

const HOME_HREF = "/teacher/dashboard"

const navItems = [
  { href: "/teacher/dashboard",  icon: "🏠", label: "工作台" },
  { href: "/teacher/classes",    icon: "👥", label: "班級管理" },
  { href: "/teacher/upload",     icon: "📤", label: "上傳教材" },
  { href: "/teacher/review",     icon: "✅", label: "AI 審核" },
  { href: "/teacher/publish",    icon: "📢", label: "發佈作業" },
  { href: "/teacher/classroom",  icon: "🎓", label: "課堂模式" },
  { href: "/teacher/placement-bank", icon: "🎯", label: "測評題庫" },
  { href: "/teacher/redemptions", icon: "🎁", label: "兌換管理", badgeKey: "redemptions" },
  { href: "/teacher/reports/class", icon: "📊", label: "班級報告" },
  { href: "/teacher/settings",   icon: "⚙️", label: "設置" },
]

export default function SideNav() {
  const pathname = usePathname()
  const { user, token, logout } = useAuth()
  const [pendingCount, setPendingCount] = useState(0)

  useEffect(() => {
    if (!token || user?.role !== "teacher") return
    let alive = true
    const tick = async () => {
      try {
        const r = await api.get<unknown[]>("/api/redemptions/pending", { token })
        if (alive) setPendingCount(Array.isArray(r) ? r.length : 0)
      } catch {}
    }
    void tick()
    const id = setInterval(tick, 60_000)
    return () => {
      alive = false
      clearInterval(id)
    }
  }, [token, user?.role])

  function handleLogout() {
    logout()
    window.location.assign("/teacher/login")
  }

  return (
    <aside className="w-60 min-h-screen bg-surface border-r border-border/60 flex flex-col">
      {/* Logo */}
      <div className="px-6 py-5 border-b border-border/60 space-y-2.5">
        <Link
          href={HOME_HREF}
          aria-label="回到工作台"
          className="flex items-center gap-3 rounded-2xl -mx-2 px-2 py-1 transition-colors hover:bg-primary/5"
        >
          <div className="w-10 h-10 rounded-2xl bg-primary flex items-center justify-center text-white text-lg font-bold">
            宇
          </div>
          <div>
            <p className="text-sm font-bold text-text leading-tight">語宙 YUZHOU</p>
            <p className="text-xs text-text-muted">教師端</p>
          </div>
        </Link>
        {user?.schoolName && (
          <p className="text-sm font-semibold text-text leading-snug">
            {user.schoolName}
          </p>
        )}
      </div>

      {/* Nav items */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {navItems.map((item) => {
          const active = pathname.startsWith(item.href)
          const showBadge = item.badgeKey === "redemptions" && pendingCount > 0
          return (
            <Link
              key={item.href}
              href={item.href}
              className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-200 text-sm font-medium ${
                active
                  ? "bg-primary/10 text-primary"
                  : "text-text-muted hover:bg-border/50 hover:text-text"
              }`}
            >
              <span className="text-lg">{item.icon}</span>
              <span className="flex-1">{item.label}</span>
              {showBadge && (
                <span
                  className="text-xs font-bold rounded-full px-1.5 min-w-[20px] text-center"
                  style={{ background: "#FF6B6B", color: "white" }}
                >
                  {pendingCount}
                </span>
              )}
            </Link>
          )
        })}
      </nav>

      {/* Footer: user identity + logout */}
      <div className="px-3 py-4 border-t border-border/60 space-y-2">
        {user && (
          <div className="px-4 py-2 text-xs text-text-muted truncate">
            {user.displayName}
          </div>
        )}
        <button
          type="button"
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-text-muted hover:bg-error/10 hover:text-error transition-all duration-200 cursor-pointer"
        >
          <span className="text-lg">🚪</span>
          登出
        </button>
      </div>
    </aside>
  )
}
